/**
 * Comment review packet (F5c) — the feedback, as text a person or an agent
 * can act on.
 *
 * Pure functions of `(threads, meta)`: no DOM, no network, no geometry. The
 * markdown is deliberately LLM-shaped — a preamble that says what the file is,
 * then one section per thread with its page, status and full conversation — so
 * "export the review and hand it to the agent" is a real workflow rather than
 * a copy-paste of a screenshot.
 *
 * Unlike the panel's Copy button, an export covers EVERY thread. Copy takes
 * what is on screen because the filter is part of that intent; a packet that
 * silently omitted resolved threads would be a misleading record.
 *
 * The annotated-screenshot half (badges drawn onto the page shot, plus
 * per-comment crops) is deferred: it needs the same server capture the minimap
 * uses, and the text packet is worth shipping before it.
 */

/** ISO date, or empty when the timestamp is missing/unparseable. */
function isoDay(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n <= 0) return '';
  const d = new Date(n);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
}

/**
 * Markdown blockquote for a body, so a comment containing '#' or '- ' cannot
 * restructure the document that quotes it.
 */
function quote(text) {
  return String(text ?? '')
    .split('\n')
    .map((line) => `> ${line}`)
    .join('\n');
}

/**
 * @param {Array<object>} threads
 * @param {{projectName?: string, version?: number, numbers?: Map<string, number>,
 *   pageNameFor?: (id: string) => string, anchorFor?: (t: object) => string}} [meta]
 * @returns {string}
 */
export function buildReviewMarkdown(threads, meta = {}) {
  const { projectName = 'Project', version = null, numbers = null, pageNameFor, anchorFor } = meta;
  const list = Array.isArray(threads) ? threads : [];
  const numberOf = (t, i) => numbers?.get(t.id) ?? i + 1;

  const open = list.filter((t) => t.status !== 'resolved').length;
  const out = [
    `# ${projectName} — review notes`,
    '',
    `${list.length} comment${list.length === 1 ? '' : 's'}, ${open} open${
      version ? ` · version ${version}` : ''
    }`,
    '',
    'Each entry below is one comment thread pinned to a place in the site. The',
    '"Pinned to" line says what it is attached to; "orphaned" means the element',
    'it pointed at no longer exists in this version.',
    ''
  ];

  if (!list.length) {
    out.push('_No comments._');
    return out.join('\n');
  }

  for (const [i, thread] of list.entries()) {
    const [first, ...replies] = thread.comments || [];
    const page = pageNameFor?.(thread.pageId) || thread.pageId || 'unknown page';
    const status = thread.status === 'resolved' ? 'resolved' : 'open';
    const day = isoDay(thread.createdAt);

    out.push(`## ${numberOf(thread, i)}. ${page} — ${status}`);
    out.push('');
    out.push(`- **Author:** ${first?.authorName || 'Unknown'}${day ? ` · ${day}` : ''}`);
    out.push(`- **Pinned to:** ${anchorFor?.(thread) || 'this section'}`);
    out.push('');
    out.push(quote(first?.body || '(no comment)'));
    out.push('');
    for (const reply of replies) {
      const replyDay = isoDay(reply.createdAt);
      out.push(`**${reply.authorName || 'Reply'}**${replyDay ? ` · ${replyDay}` : ''}`);
      out.push('');
      out.push(quote(reply.body || ''));
      out.push('');
    }
  }
  return out.join('\n').trimEnd() + '\n';
}

/**
 * The same data, machine-readable. Stable field order, no wall-clock and no
 * ids the consumer cannot resolve — a diffable sibling of the markdown.
 */
export function buildReviewJson(threads, meta = {}) {
  const { projectName = 'Project', version = null, numbers = null, pageNameFor, anchorFor } = meta;
  const list = Array.isArray(threads) ? threads : [];
  return {
    project: projectName,
    version: version ?? null,
    total: list.length,
    open: list.filter((t) => t.status !== 'resolved').length,
    comments: list.map((thread, i) => ({
      number: numbers?.get(thread.id) ?? i + 1,
      id: thread.id,
      page: pageNameFor?.(thread.pageId) || thread.pageId || null,
      status: thread.status === 'resolved' ? 'resolved' : 'open',
      createdAt: isoDay(thread.createdAt) || null,
      anchor: anchorFor?.(thread) || 'section',
      messages: (thread.comments || []).map((c) => ({
        author: c.authorName || null,
        authorKind: c.authorKind || null,
        createdAt: isoDay(c.createdAt) || null,
        body: c.body ?? ''
      }))
    }))
  };
}

/** Filename stem: lowercase, no separators that a filesystem would argue with. */
export function reviewPacketBasename(projectName, version) {
  const slug =
    String(projectName || 'project')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'project';
  return version ? `${slug}-v${version}-review` : `${slug}-review`;
}
